// PATH: src/utils/googleIdentity.js   (NEW FILE)
// "Sign in with Google" WITHOUT Firebase — Google Identity Services (GIS)
// straight from Google. Gives a Google-signed ID token (JWT) to JavaScript.
import { jwtDecode } from "jwt-decode";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GSI_SRC = "https://accounts.google.com/gsi/client";

let scriptPromise = null;
let initialized = false;
let credentialHandler = null;

// Loads Google's script once.
export const loadGoogleScript = () => {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => {
      scriptPromise = null;
      script.remove();
      reject(new Error("Couldn't load Google sign-in. Check your internet and try again."));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
};

// Google wants initialize() called only ONCE, so the callback is a stable
// wrapper and React swaps the real handler through this setter.
export const setCredentialHandler = (fn) => {
  credentialHandler = fn;
};

export const initGoogleIdentity = async () => {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error("VITE_GOOGLE_CLIENT_ID is missing in your .env file");
  }

  const google = await loadGoogleScript();

  if (!initialized) {
    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response) => credentialHandler?.(response),

      // One Tap card (top-right): returning users with ONE Google account are
      // signed in automatically; with several accounts the user picks one there.
      auto_select: true,
      cancel_on_tap_outside: false, // clicking elsewhere must not dismiss it

      // The "Continue with Google" BUTTON: use the browser's own account
      // chooser (FedCM) instead of opening the accounts.google.com popup window.
      // Chrome desktop M125+ / Android M128+ only; other browsers fall back to
      // Google's popup because they can't do it any other way.
      use_fedcm_for_button: true,
      // returning users with an active Google session skip the chooser entirely
      button_auto_select: true,
    });
    initialized = true;
  }

  return google;
};

export const renderGoogleButton = (element, options = {}) => {
  window.google?.accounts?.id?.renderButton(element, {
    type: "standard",
    theme: "outline",
    size: "large",
    text: "continue_with",
    shape: "pill",
    logo_alignment: "left",
    width: 300,
    ...options,
  });
};

// One Tap prompt (small card inside the browser, no separate window)
export const showOneTap = () => {
  window.google?.accounts?.id?.prompt();
};

export const cancelOneTap = () => {
  try {
    window.google?.accounts?.id?.cancel();
  } catch {
    /* ignore */
  }
};

// Call on logout, otherwise One Tap would sign the same user straight back in
export const disableGoogleAutoSelect = () => {
  try {
    window.google?.accounts?.id?.disableAutoSelect();
  } catch {
    /* ignore */
  }
};

// Decodes the Google ID token and runs the checks that are possible in the
// browser. NOTE: the real signature check can only be done by the backend.
export const readGoogleCredential = (credential) => {
  if (typeof credential !== "string" || !credential) {
    throw new Error("Google did not return a sign-in token.");
  }

  const p = jwtDecode(credential);

  if (!["accounts.google.com", "https://accounts.google.com"].includes(p.iss)) {
    throw new Error("Invalid Google token (issuer).");
  }
  if (p.aud !== GOOGLE_CLIENT_ID) {
    throw new Error("Invalid Google token (client).");
  }
  if (typeof p.exp !== "number" || p.exp * 1000 < Date.now()) {
    throw new Error("Google sign-in expired. Please try again.");
  }
  if (!p.sub || !p.email) {
    throw new Error("Google did not share your email. Please try again.");
  }
  if (p.email_verified === false) {
    throw new Error("Your Google email is not verified.");
  }

  const fullName = (p.name || "").trim();
  const [fallbackFirst = "", ...fallbackRest] = fullName.split(/\s+/);

  return {
    socialId: p.sub, // same value Firebase used as providerData[0].uid
    email: p.email,
    firstName: p.given_name || fallbackFirst,
    lastName: p.family_name || fallbackRest.join(" "),
    photoUrl: p.picture || "",
  };
};